<template>
  <div class="showroom-banner">
    <input
      id="bannerFileInput"
      ref="bannerFileInput"
      class="hidden-file-input"
      type="file"
      :disabled="uploadInProgress"
      @change="onImageChange"
    />
    <label for="bannerFileInput">
      <div v-if="uploadInProgress || hasBanner" class="banner-preview-wrapper">
        <LoadingHeart v-if="uploadInProgress" />
        <div v-else class="banner-preview">
          <img :src="bannerUrl" alt="banner image" class="background" />
          <img :src="bannerUrl" alt="banner image" class="foreground" />
          <div class="upload-indicator-wrapper">
            <div class="upload-indicator">
              <Icon icon="image-upload" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="btn btn-ghost icon-btn">
        <Icon icon="image-upload" />
        {{ $t('POS.SHOP_SETTINGS.STARTPAGE.TEASER.IMAGE.UPLOAD_IMG') }}
      </div>
    </label>
    <p v-if="displayUploadError" class="text-sm error-text">
      <Icon icon="error" /> {{ $t('SHOWROOM_ADMIN.BANNER.ERROR') }}
    </p>
    <p class="text-sm text-grey">
      {{ $t('SHOWROOM_ADMIN.BANNER.REQUIREMENTS') }}: 2400x600px, 10 MB
    </p>
  </div>
</template>

<script setup lang="ts">
import userService from 'commons/api/userService/userService';
import LoadingHeart from 'commons/LoadingHeart/LoadingHeart.vue';
import { useMarketplaceStore } from 'commons/store/marketplace';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import analyticsService from 'commons/tracking/analytics';

const store = useStore();
const marketplaceStore = useMarketplaceStore();

const hasBanner = computed(
  () => marketplaceStore.showroomData?.userImages.hasBanner,
);
const bannerTimestamp = ref(Date.now());
const bannerUrl = computed(
  () =>
    `/image-server/v1/users/${store.state.user.data.id}/banner,maxWidth=2000,version=${bannerTimestamp.value}.jpg`,
);
const uploadInProgress = ref(false);
const displayUploadError = ref(false);

const onImageChange = async (e) => {
  displayUploadError.value = false;
  const file = e.srcElement.files[0];
  const isChange = hasBanner.value;
  uploadInProgress.value = true;

  try {
    await userService.updateBannerImage(file);
    bannerTimestamp.value = Date.now();

    await getImageDimensionsFromUrl(bannerUrl.value).then(
      ({ width, height }) => {
        marketplaceStore.setUserBanner({
          hasBanner: true,
          bannerWidth: width,
          bannerHeight: height,
          bannerVersion: bannerTimestamp.value.toString(),
        });
      },
    );

    analyticsService.logEvent(
      isChange
        ? 'showroom_change_header_image'
        : 'showroom_upload_header_image',
    );
  } catch (error) {
    displayUploadError.value = true;
  }
  uploadInProgress.value = false;
};

const getImageDimensionsFromUrl = (
  imageUrl: string,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = imageUrl;
  });
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.showroom-banner {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

p {
  margin: 0;
}

img {
  max-height: 135px;
  max-width: 100%;
}

.banner-preview-wrapper {
  padding: 12px;
  border-radius: $border-radius-medium;
  border: 1px solid $grey40;
}

.banner-preview {
  height: 135px;
  max-width: 100%;
  position: relative;
  border-radius: $border-radius-small;
  overflow: hidden;
  display: flex;
  justify-content: center;

  .upload-indicator-wrapper {
    display: none;
  }

  &:hover .upload-indicator-wrapper {
    display: flex;
    z-index: 2;
    background-color: rgba(0, 0, 0, 0.8);
    cursor: pointer;
    justify-content: center;
    align-items: center;
    position: absolute;
    object-fit: cover;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    .upload-indicator {
      color: $pa-color-main;
      padding: 8px;
      border-radius: 100%;
      background-color: $white;

      .icon {
        width: 28px;
        height: 28px;
      }
    }
  }
}

img.background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  filter: blur(50px);
}

img.foreground {
  z-index: 1;
}

.btn {
  display: inline-flex;
  padding: 12px 16px;
}

.error-text {
  display: flex;
  align-items: center;
  gap: 4px;
  color: $pa-color-red;
}
</style>
